import React from 'react'
import { Box, Grid, ClickAwayListener } from '@mui/material'
import ImagesCarousel from '../Lists/ListCard/ImagesCarousel'
import ListingName from './ListingName'
import ListingNotes from './ListingNotes'
import ListingDescription from './ListingDescription'
import ListingDescriptionMobile from './Mobile/ListingDescription'
import { AdminPropertyAdWithList } from './types'
import ListingIcons from './ListingIcons'
import ListingActions from './ListingActions'
import { PROPERTY_AD_STATUS } from 'helpers/constants'
import { useTranslation } from 'react-i18next'

interface Props {
  propertyAd: AdminPropertyAdWithList
  onDelete: () => void
  matchesSM: boolean
  matchesTablet: boolean
  matchesLaptop: boolean
  onUpdate: (
    name: string | null,
    notes: string | null,
    propertyAdAlternativeId: string | null
  ) => void
  listId: number | null
}
const Listing = (props: Props) => {
  const {
    propertyAd,
    onDelete,
    listId,
    onUpdate,
    matchesSM,
    matchesTablet,
    matchesLaptop,
  } = props
  const { t } = useTranslation()

  const [isExpanded, setIsExpanded] = React.useState(false)

  const handleClickAway = () => {
    if (isExpanded) {
      setIsExpanded(false)
    }
  }

  const handleClickIn = () => {
    if (!isExpanded) setIsExpanded(true)
  }

  const PROPERTY_IS_INACTIVE =
    propertyAd.status !== PROPERTY_AD_STATUS.STATUS_ACTIVE

  return (
    <Box
      sx={{
        pointerEvents: PROPERTY_IS_INACTIVE ? 'none' : 'auto',
        zIndex: 1,
        position: 'relative',
        width: '100%',
      }}
    >
      {PROPERTY_IS_INACTIVE ? (
        <Box
          sx={{
            position: 'absolute',
            display: 'grid',
            placeItems: 'center',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 2,
            fontWeight: 700,
            fontSize: '16px',
          }}
        >
          {t(`ListPage.inactive`)}
        </Box>
      ) : null}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box
          sx={{
            // width: '100%',
            opacity: PROPERTY_IS_INACTIVE ? '0.4' : '1',
          }}
          onClick={() => (isExpanded ? handleClickAway() : handleClickIn())}
        >
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-evenly"
            gap={{ xs: '12px', md: '21px' }}
            sx={{
              bgcolor: 'common.white',
              borderRadius: '20px',
              borderColor: 'secondary',
              boxShadow: '8px 8px 20px 0px #09090959',
              p: '12px',
              mb: isExpanded ? '17px' : 0,
              flexWrap: 'no-wrap',
              border: '3px solid transparent',
              maxWidth: { xs: '360px', sm: '100%', md: '741px', lg: '100%' },
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
              },
              transition: (theme) => theme.transitions.create(['border-color']),
            }}
          >
            <Grid item xs={12} md="auto">
              <Grid
                container
                columnGap={'13px'}
                sx={{ flexWrap: matchesSM || isExpanded ? 'wrap' : 'nowrap' }}
              >
                {/* {matchesSM && isExpanded ? (
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent={'space-between'}>
                      <span
                        onClick={(e) =>
                          matchesSM ? undefined : e.stopPropagation()
                        }
                      >
                        <ListingName
                          onUpdate={onUpdate}
                          name={propertyAd?.favorite?.name ?? ''}
                          notes={propertyAd?.favorite?.notes || null}
                          propertyAdAlternativeId={
                            propertyAd?.favorite?.entityId || null
                          }
                          listId={listId}
                          propertyAd={propertyAd}
                          matchesSM={matchesSM}
                        />
                      </span>
                      <ListingDelete
                        listId={listId}
                        propertyAd={propertyAd}
                        onDelete={onDelete}
                        isExpanded={isExpanded}
                        listName={propertyAd?.favorite?.name ?? ''}
                      />
                    </Stack>
                  </Grid>
                ) : null} */}
                <Grid item xs="auto" onClick={(e) => e.stopPropagation()}>
                  <ImagesCarousel
                    options={
                      propertyAd?.attachments
                        ?.map((item) => item.filePath)
                        .filter((item): item is string => !!item) ?? []
                    }
                    width={matchesSM ? 152 : isExpanded ? 244 : 108}
                    height={matchesSM ? 124 : isExpanded ? 191 : 90}
                    errorHeight={isExpanded ? '130px' : '50px'}
                    errorWidth={isExpanded ? '130px' : '50px'}
                  />
                </Grid>
                <Grid item xs sx={{ display: { xs: 'flex-item', md: 'none' } }}>
                  <ListingDescriptionMobile
                    isExpanded={isExpanded}
                    propertyAd={propertyAd}
                    matchesSM={matchesSM}
                    matchesTablet={matchesTablet}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    listId={listId}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs="auto"
              sx={{ display: matchesSM && !isExpanded ? 'none' : 'flex-item' }}
            >
              <Grid
                container
                rowGap={0}
                sx={{ width: '350px', display: { xs: 'none', md: 'block' } }}
              >
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ListingName
                      matchesSM={matchesSM}
                      onUpdate={onUpdate}
                      name={propertyAd?.favorite?.name ?? ''}
                      notes={propertyAd?.favorite?.notes || null}
                      propertyAdAlternativeId={
                        propertyAd?.favorite?.entityId || null
                      }
                      listId={listId}
                      propertyAd={propertyAd}
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ mt: '12px', display: { xs: 'none', md: 'flex-item' } }}
                >
                  <ListingDescription
                    matchesTablet={matchesTablet}
                    matchesSM={matchesSM}
                    propertyAd={propertyAd}
                  />
                </Grid>
                {isExpanded ? (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      mt: '22px',
                      display: { xs: 'none', md: 'flex-item' },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ListingNotes
                      onUpdate={onUpdate}
                      notes={propertyAd?.favorite?.notes ?? ''}
                      name={propertyAd?.favorite?.name ?? null}
                      propertyAdAlternativeId={
                        propertyAd?.favorite?.entityId ?? null
                      }
                      listId={listId}
                    />
                  </Grid>
                ) : null}
                {/* </Grid> */}
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              md={true}
              sx={{
                display: matchesSM && !isExpanded ? 'none' : 'flex-item',
              }}
            >
              <ListingIcons
                matchesLaptop={matchesLaptop}
                matchesSM={matchesSM}
                matchesTablet={matchesTablet}
                propertyAd={propertyAd}
                isExpanded={isExpanded}
              />
            </Grid>

            <Grid
              item
              xs={matchesSM ? true : 'auto'}
              sx={{ display: matchesSM && !isExpanded ? 'none' : 'flex-item' }}
            >
              <ListingActions
                matchesSM={matchesSM}
                matchesTablet={matchesTablet}
                onPrevent={(e) => e.stopPropagation()}
                listId={listId}
                propertyAd={propertyAd}
                onDelete={onDelete}
                isExpanded={isExpanded}
                listName={propertyAd?.favorite?.name ?? ''}
              />
            </Grid>
            {isExpanded ? (
              <Grid
                item
                xs={12}
                sx={{
                  mt: { xs: '0px', md: '22px' },
                  display: { xs: 'flex-item', md: 'none' },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <ListingNotes
                  onUpdate={onUpdate}
                  notes={propertyAd?.favorite?.notes ?? ''}
                  name={propertyAd?.favorite?.name ?? null}
                  propertyAdAlternativeId={
                    propertyAd?.favorite?.entityId ?? null
                  }
                  listId={listId}
                />
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </ClickAwayListener>
    </Box>
  )
}

export default Listing
